import { api } from '../api/shop';
import type { MyVoucher, OfflinePass } from '../api/types';

/**
 * Offline passes the website has fetched and is keeping. SOW TC-03 / TC-04.
 *
 * A pass is a short-lived signed token for one voucher that an offline till can
 * verify with no connection. The site fetches one quietly whenever it is online and
 * a voucher is spendable, and keeps it so that when the signal drops at the counter
 * the customer still has something to show.
 *
 * WHERE IT IS KEPT, and why not localStorage: sessionStorage, the same choice and
 * the same reasoning as the refresh token (api/client.ts). A pass is bearer material
 * for one voucher's offline spend; tab-scoped storage means a shared computer does
 * not keep it after the tab closes. Inside the tab it survives navigating between
 * the voucher list and a voucher with no connection - which is the moment it is for.
 * The customer who wants a copy that outlives the tab saves the image themselves.
 */

export interface KeptPass extends OfflinePass {
  voucherId: string;
  dealTitle: string;
  businessName: string;
  savedAt: string;
}

const KEY = 'nf.offline-passes';

function readAll(): Record<string, KeptPass> {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (raw === null) return {};
    const parsed = JSON.parse(raw) as Record<string, KeptPass>;
    const now = Date.now();

    // Expired passes are dead weight and a till would refuse them anyway.
    return Object.fromEntries(
      Object.entries(parsed).filter(([, p]) => new Date(p.expiresAt).getTime() > now),
    );
  } catch {
    return {};
  }
}

function writeAll(passes: Record<string, KeptPass>): void {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(passes));
  } catch {
    // Storage full or disabled. The pass still shows while this page is open.
  }
}

export function keptPass(voucherId: string): KeptPass | null {
  return readAll()[voucherId] ?? null;
}

export function forgetPass(voucherId: string): void {
  const all = readAll();
  delete all[voucherId];
  writeAll(all);
}

/**
 * Fetches a fresh pass and keeps it. Null when the voucher has none to give - the
 * deployment has no signing key, the voucher is not spendable, or we are offline -
 * in which case any pass already kept is left alone.
 */
export async function refreshPass(voucher: Pick<MyVoucher, 'id' | 'dealTitle' | 'businessName'>): Promise<KeptPass | null> {
  try {
    const pass = await api.offlinePass(voucher.id);
    const kept: KeptPass = {
      ...pass,
      voucherId: voucher.id,
      dealTitle: voucher.dealTitle,
      businessName: voucher.businessName,
      savedAt: new Date().toISOString(),
    };

    const all = readAll();
    all[voucher.id] = kept;
    writeAll(all);
    return kept;
  } catch {
    return null;
  }
}

/** Quietly keeps a pass for every spendable voucher. Called from the voucher list. */
export async function refreshPasses(vouchers: MyVoucher[]): Promise<void> {
  if (!navigator.onLine) return;

  const spendable = vouchers
    .filter((v) => v.status === 'active' && v.balanceCents > 0 && new Date(v.validTo).getTime() > Date.now())
    .slice(0, 20);

  for (const v of spendable) {
    const existing = keptPass(v.id);

    // Fresh enough: more than a third of its life left. Saves a signature per
    // voucher per page view.
    if (existing !== null) {
      const left = new Date(existing.expiresAt).getTime() - Date.now();
      const life = new Date(existing.expiresAt).getTime() - new Date(existing.savedAt).getTime();
      if (life > 0 && left > life / 3) continue;
    }

    await refreshPass(v);
  }
}
